import axios, { CancelToken } from 'axios'
import querystring from 'qs'
import { CANCEL } from 'redux-saga'

import { normalizeError } from './utils'

const elasticService = () => {
  const fetchIndices = async (accessToken) => {
    try {
      const config = { headers: { Authorization: `Bearer ${accessToken}` } }
      const response = await axios.get(`/api/elastic/indices`, config)
      return response.data
    } catch (error) {
      throw normalizeError(error)
    }
  }

  const fetchAliases = async (accessToken) => {
    try {
      const config = { headers: { Authorization: `Bearer ${accessToken}` } }
      const response = await axios.get(`/api/elastic/aliases`, config)
      return response.data
    } catch (error) {
      throw normalizeError(error)
    }
  }

  const fetchTemplates = async (accessToken) => {
    try {
      const config = { headers: { Authorization: `Bearer ${accessToken}` } }
      const response = await axios.get(`/api/elastic/templates`, config)
      return response.data
    } catch (error) {
      throw normalizeError(error)
    }
  }

  const fetchIndex = async (accessToken, index) => {
    try {
      const config = { headers: { Authorization: `Bearer ${accessToken}` } }
      const response = await axios.get(`/api/elastic/index/${index}`, config)
      return response.data
    } catch (error) {
      throw normalizeError(error)
    }
  }

  const fetchAlias = async (accessToken, aliasName) => {
    try {
      const config = { headers: { Authorization: `Bearer ${accessToken}` } }
      const response = await axios.get(`/api/elastic/alias/${aliasName}`, config)
      return response.data
    } catch (error) {
      throw normalizeError(error)
    }
  }

  const fetchTemplate = async (accessToken, templateName) => {
    try {
      const config = { headers: { Authorization: `Bearer ${accessToken}` } }
      const response = await axios.get(`/api/elastic/template/${templateName}`, config)
      return response.data
    } catch (error) {
      throw normalizeError(error)
    }
  }

  const addIndex = async (accessToken, indexName) => {
    try {
      const config = { headers: { Authorization: `Bearer ${accessToken}` } }
      await axios.put(`/api/elastic/index/${indexName}`, null, config)
    } catch (error) {
      throw normalizeError(error)
    }
  }

  const updateIndex = async (accessToken, indexName, mapping) => {
    try {
      const config = { headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'text/plain; charset=UTF-8' } }
      await axios.post(`/api/elastic/index/${indexName}`, mapping, config)
    } catch (error) {
      throw normalizeError(error)
    }
  }

  const removeIndex = async (accessToken, indexName) => {
    try {
      const config = { headers: { Authorization: `Bearer ${accessToken}` } }
      await axios.delete(`/api/elastic/index/${indexName}`, config)
    } catch (error) {
      throw normalizeError(error)
    }
  }

  const addAlias = async (accessToken, aliasName, indexName) => {
    try {
      const config = { headers: { Authorization: `Bearer ${accessToken}` } }
      await axios.put(`/api/elastic/alias/${aliasName}/${indexName}`, null, config)
    } catch (error) {
      throw normalizeError(error)
    }
  }

  const updateAlias = async (accessToken, aliasName, indexName) => {
    try {
      const config = { headers: { Authorization: `Bearer ${accessToken}` } }
      await axios.post(`/api/elastic/alias/${aliasName}/${indexName}`, null, config)
    } catch (error) {
      throw normalizeError(error)
    }
  }

  const removeAlias = async (accessToken, aliasName, indexName) => {
    try {
      const config = { headers: { Authorization: `Bearer ${accessToken}` } }
      await axios.delete(`/api/elastic/alias/${aliasName}/${indexName}`, config)
    } catch (error) {
      throw normalizeError(error)
    }
  }

  const addTemplate = async (accessToken, templateName, template) => {
    try {
      const config = { headers: { Authorization: `Bearer ${accessToken}` } }
      await axios.put(`/api/elastic/template/${templateName}`, template, config)
    } catch (error) {
      throw normalizeError(error)
    }
  }

  const updateTemplate = async (accessToken, templateName, template) => {
    try {
      const config = { headers: { Authorization: `Bearer ${accessToken}` } }
      await axios.post(`/api/elastic/template/${templateName}`, template, config)
    } catch (error) {
      throw normalizeError(error)
    }
  }

  const removeTemplate = async (accessToken, templateName) => {
    try {
      const config = { headers: { Authorization: `Bearer ${accessToken}` } }
      await axios.delete(`/api/elastic/template/${templateName}`, config)
    } catch (error) {
      throw normalizeError(error)
    }
  }

  const aggregateByBrands = (accessToken, contentType, field, interval) => {
    try {
      const source = CancelToken.source()
      const config = { headers: { Authorization: `Bearer ${accessToken}` }, cancelToken: source.token }
      const request = axios.get(`/api/elastic/aggs/brands?${querystring.stringify({ contentType, field, interval })}`, config)
      request[CANCEL] = () => source.cancel()
      return request
    } catch (error) {
      throw normalizeError(error)
    }
  }

  const aggregateByContentTypes = (accessToken, brand, field, interval) => {
    try {
      const source = CancelToken.source()
      const config = { headers: { Authorization: `Bearer ${accessToken}` }, cancelToken: source.token }
      const request = axios.get(`/api/elastic/aggs/contentTypes?${querystring.stringify({ brand, field, interval })}`, config)
      request[CANCEL] = () => source.cancel()
      return request
    } catch (error) {
      throw normalizeError(error)
    }
  }

  const aggregateByDocumentCounts = (accessToken, brand, contentType, field, interval) => {
    try {
      const source = CancelToken.source()
      const config = { headers: { Authorization: `Bearer ${accessToken}` }, cancelToken: source.token }
      const request = axios.get(`/api/elastic/aggs/documents?${querystring.stringify({ brand, contentType, field, interval })}`, config)
      request[CANCEL] = () => source.cancel()
      return request
    } catch (error) {
      throw normalizeError(error)
    }
  }

  return {
    fetchIndices,
    fetchAliases,
    fetchTemplates,
    fetchIndex,
    fetchAlias,
    fetchTemplate,
    addIndex,
    updateIndex,
    removeIndex,
    addAlias,
    updateAlias,
    removeAlias,
    addTemplate,
    updateTemplate,
    removeTemplate,
    aggregateByBrands,
    aggregateByContentTypes,
    aggregateByDocumentCounts,
  }
}

export default elasticService()
